import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import type Owner from '@ember/owner';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import type { IntlService } from 'ember-intl';
import { VStack, Section } from 'tio-ui/components/layout';
import Select from 'tio-ui/components/select';
import Text from 'tio-ui/components/text';
import type { NotificationsService } from 'tio-ui/components/notifications';
import { LANGUAGE_OPTIONS, isSupportedLocale } from 'tio-employee/services/locale-preference';
import type LocalePreferenceService from 'tio-employee/services/locale-preference';
import type { SupportedLocale } from 'tio-employee/services/locale-preference';

type LocaleKey = SupportedLocale | 'auto';

interface LocaleOption {
  key: LocaleKey;
  label: string;
}

const LOCALE_OPTIONS: LocaleOption[] = [
  { key: 'auto', label: 'Auto-detect (browser)' },
  ...LANGUAGE_OPTIONS,
];

/** The Profile language setting; a choice is saved on the account, and Auto-detect clears it. */
export default class ProfileLanguageSection extends Component {
  @service declare intl: IntlService;
  @service declare localePreference: LocalePreferenceService;
  @service declare notifications: NotificationsService;
  @tracked selectedLocale: LocaleKey;

  constructor(owner: Owner, args: object) {
    super(owner, args);

    const override = localStorage.getItem('locale');
    this.selectedLocale =
      this.localePreference.saved ?? (isSupportedLocale(override) ? override : 'auto');
  }

  get localeOptions() {
    return LOCALE_OPTIONS;
  }

  handleLocaleChange = (selectedKey: string) => {
    void this.changeLocale.perform(selectedKey as LocaleKey);
  };

  changeLocale = dropTask(async (key: LocaleKey) => {
    const previous = this.selectedLocale;
    this.selectedLocale = key;

    let remembered: boolean;
    try {
      remembered = await this.localePreference.save(key === 'auto' ? null : key);
    } catch (error) {
      console.error(error);
      this.selectedLocale = previous;
      this.notifications.add(this.intl.t('profile.language.save_error'), { appearance: 'error' });
      return;
    }

    if (remembered) this.localePreference.reload();
  });

  <template>
    <Section>
      <:header>{{t "profile.language.title"}}</:header>
      <:body>
        <VStack class="gap-3">
          <Text @variant="muted" @size="sm">{{t "profile.language.description"}}</Text>
          <Select
            @items={{this.localeOptions}}
            @selected={{this.selectedLocale}}
            @onChange={{this.handleLocaleChange}}
            @disabled={{this.changeLocale.isRunning}}
            class="w-full max-w-xs"
            data-test-profile-language-select
          />
        </VStack>
      </:body>
    </Section>
  </template>
}
